<script lang="ts">
  import { onDestroy } from 'svelte';
  import { push } from "svelte-spa-router";
  import { logout } from "../lib/api";
  import { clearUser, currentUser } from "../lib/stores/auth";
  import { clearCachedVoters } from "../lib/stores/voters";
  import ChangePasswordModal from "./ChangePasswordModal.svelte";
  import VolunteerStats from "./VolunteerStats.svelte";

  let menuOpen = false;
  let showChangePassword = false;
  let showStats = false;
  let menuRoot: HTMLDivElement;

  function toggleMenu() { menuOpen = !menuOpen; }
  function closeMenu() { menuOpen = false; }

  function onDocClick(e: MouseEvent) {
    if (!menuOpen) return;
    if (menuRoot && !menuRoot.contains(e.target as Node)) closeMenu();
  }
  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') closeMenu();
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    onDestroy(() => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    });
  }

  function openChangePassword() {
    showChangePassword = true;
    closeMenu();
  }

  function openStats() {
    showStats = true;
    closeMenu();
  }

  async function handleSignOut() {
    closeMenu();
    // Filters are left as they are: resetting them here would make the map
    // and an open list reload during sign-out. The next sign-in resets them
    // (setUser in lib/stores/auth.ts).
    await logout();
    clearUser();
    // Await so the next sign-in on a shared device can't see the previous
    // user's in-memory voter list.
    await clearCachedVoters();
    push("/login");
  }

  function gotoAdmin() {
    closeMenu();
    push('/admin/status');
  }
</script>

<div class="account-menu" bind:this={menuRoot}>
  {#if $currentUser}
    <button
      class="trigger"
      class:open={menuOpen}
      on:click={toggleMenu}
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      type="button"
    >
      <span class="display-name">{$currentUser.display_name ?? $currentUser.username}</span>
      <svg class="caret" viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
        <path d="M2 4 L6 8 L10 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>

    {#if menuOpen}
      <div class="dropdown" role="menu">
        <div class="dd-head">
          <span class="dd-name">{$currentUser.display_name ?? $currentUser.username}</span>
          <span class="dd-user mono">@{$currentUser.username}</span>
          <span class="dd-role">{$currentUser.role}</span>
        </div>
        <button class="dd-item" role="menuitem" type="button" on:click={openStats}>
          My stats
        </button>
        <button class="dd-item" role="menuitem" type="button" on:click={() => { closeMenu(); push('/results'); }}>
          Election results
        </button>
        <button class="dd-item" role="menuitem" type="button" on:click={openChangePassword}>
          Change password
        </button>
        {#if $currentUser.role === 'admin'}
          <button class="dd-item" role="menuitem" type="button" on:click={gotoAdmin}>
            Admin dashboard
          </button>
        {/if}
        <div class="dd-divider"></div>
        <button class="dd-item dd-signout" role="menuitem" type="button" on:click={handleSignOut}>
          Sign out
        </button>
      </div>
    {/if}
  {/if}
</div>

{#if showChangePassword}
  <ChangePasswordModal
    on:close={() => (showChangePassword = false)}
    on:done={() => (showChangePassword = false)}
  />
{/if}

{#if showStats}
  <button class="stats-backdrop" on:click={() => (showStats = false)} aria-label="Close" />
  <section class="stats-modal" role="dialog" aria-modal="true" aria-label="My canvassing stats">
    <header class="stats-head">
      <div>
        <span class="eyebrow">Volunteer</span>
        <h2>Your canvassing</h2>
      </div>
      <button class="stats-close" on:click={() => (showStats = false)} aria-label="Close">✕</button>
    </header>
    <VolunteerStats />
  </section>
{/if}

<style>
  .account-menu {
    position: relative;
    display: flex;
    align-items: center;
    font-family: var(--font-body);
  }
  .trigger {
    background: transparent;
    color: var(--paper);
    border: 1px solid rgba(247, 243, 234, 0.35);
    padding: 6px 10px 6px 12px;
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background 0.12s, border-color 0.12s;
  }
  .trigger:hover, .trigger.open {
    background: rgba(247, 243, 234, 0.1);
    border-color: rgba(247, 243, 234, 0.7);
  }
  .display-name {
    max-width: 14ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .caret { opacity: 0.85; }

  .dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: var(--paper);
    border: 1px solid var(--rule);
    box-shadow: 0 12px 28px rgba(15, 33, 27, .18);
    z-index: 200;
    padding: 4px 0;
  }
  .dd-head {
    padding: 10px 14px 12px;
    border-bottom: 1px solid var(--rule);
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 2px 8px;
    align-items: baseline;
  }
  .dd-name {
    grid-column: 1 / -1;
    font-family: var(--font-display);
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--ink);
  }
  .dd-user {
    grid-column: 1;
    font-size: 0.72rem;
    color: var(--ink-muted);
  }
  .dd-role {
    grid-column: 2;
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--leaf-ink);
    background: var(--leaf-soft);
    border: 1px solid var(--leaf-bright);
    padding: 1px 6px;
    font-weight: 700;
  }
  .dd-item {
    display: block;
    width: 100%;
    background: transparent;
    border: none;
    text-align: left;
    padding: 9px 14px;
    cursor: pointer;
    font-family: var(--font-body);
    font-size: 0.88rem;
    color: var(--ink);
    transition: background 0.1s ease;
  }
  .dd-item:hover { background: var(--paper-soft); }
  .dd-signout { color: var(--alert); font-weight: 600; }
  .dd-signout:hover { background: var(--alert-soft); }
  .dd-divider {
    height: 1px;
    background: var(--rule);
    margin: 4px 0;
  }

  @media (max-width: 520px) {
    .trigger { padding: 5px 8px 5px 10px; font-size: 0.78rem; }
    .display-name { max-width: 9ch; }
  }

  .stats-backdrop {
    position: fixed; inset: 0;
    background: rgba(15, 33, 27, 0.5);
    z-index: 1100; border: none; cursor: default;
  }
  .stats-modal {
    position: fixed;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: min(440px, 92vw);
    background: var(--paper);
    border: 1px solid var(--rule);
    box-shadow: 0 18px 40px rgba(15, 33, 27, .25);
    z-index: 1101;
    padding: 0 0 var(--s-3);
    color: var(--ink);
    max-height: 90vh;
    overflow-y: auto;
  }
  .stats-head {
    padding: var(--s-4) var(--s-4) var(--s-3);
    border-bottom: 1px solid var(--rule);
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .stats-head .eyebrow {
    display: block;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: var(--leaf-bright);
    margin-bottom: 4px;
  }
  .stats-head h2 {
    font-family: var(--font-display);
    font-size: 1.35rem;
    margin: 0;
    color: var(--ink);
  }
  .stats-close {
    background: transparent; border: none;
    font-size: 1.1rem; cursor: pointer;
    color: var(--ink-soft);
    padding: 4px 10px;
  }
  .stats-close:hover { color: var(--ink); }
</style>
